
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';

export default function PreventWaterDamagePage() {
  useEffect(() => {
    // Ensure the scroll operation runs only in a browser environment
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[400px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://public.readdy.ai/ai/img_res/3d74a715d0e682a01ca758db02472f8b.jpg"
            alt="Homeowner inspecting pipes for water damage prevention"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/55 to-black/65"></div>
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center w-full pt-28 pb-14">
          <span className="inline-block bg-sky-600/90 backdrop-blur-sm text-white text-xs font-semibold px-5 py-1.5 rounded-full mb-4">
            Prevention Tips
          </span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
            10 Ways to Prevent Water Damage in Your Home
          </h1>
          <div className="flex items-center justify-center gap-4 text-white/80 text-sm">
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line"></i> Jan 15, 2025
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line"></i> 8 min read
            </span>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-14 sm:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">

          {/* Intro */}
          <p className="text-gray-700 text-base leading-relaxed mb-6">
            Water damage is the <strong>number one threat to American homes</strong>. According to the Insurance Information Institute, approximately <strong>1 in 60 insured homes</strong> files a water damage or freezing claim each year, with the average payout nearing <strong>$14,000</strong>. The good news? The vast majority of internal water damage is preventable with routine maintenance and smart technology.
          </p>
          <p className="text-gray-700 text-base leading-relaxed mb-10">
            Here in Utah, our unique climate — with harsh winters, spring snowmelt, and summer monsoons — makes water damage prevention especially critical. Below are 10 proven strategies to protect your home and your wallet.
          </p>

          {/* Stat Callout */}
          <div className="bg-sky-50 border-l-4 border-sky-500 rounded-r-lg p-5 mb-10">
            <p className="text-sky-800 text-sm font-semibold mb-1">Key Statistic</p>
            <p className="text-gray-700 text-sm leading-relaxed">
              A tiny 1/8-inch crack in a pipe can release <strong>250 gallons of water per day</strong> — enough to cause thousands of dollars in structural damage within hours. The average U.S. household wastes <strong>10,000 gallons per year</strong> from leaks alone.
            </p>
          </div>

          {/* Tip 1 */}
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 flex items-center gap-3">
            <span className="w-8 h-8 flex items-center justify-center bg-sky-100 text-sky-600 rounded-full text-sm font-bold flex-shrink-0">1</span>
            Know Your Main Water Shut-Off Valve
          </h2>
          <p className="text-gray-700 text-sm leading-relaxed mb-8 ml-11">
            Every household member should know where the main water shut-off valve is located and how to turn it off. In an emergency, shutting off the water within the first few minutes can be the difference between a minor cleanup and a catastrophic loss. In most Utah homes, the valve is near the water meter — typically in the basement, crawl space, or near the front of the house.
          </p>

          {/* Tip 2 */}
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 flex items-center gap-3">
            <span className="w-8 h-8 flex items-center justify-center bg-sky-100 text-sky-600 rounded-full text-sm font-bold flex-shrink-0">2</span>
            Inspect Appliance Hoses Annually
          </h2>
          <p className="text-gray-700 text-sm leading-relaxed mb-8 ml-11">
            Washing machines, dishwashers, and refrigerator ice makers all rely on supply hoses that degrade over time. Rubber hoses should be replaced every <strong>5 to 7 years</strong>, even if they look fine. Upgrade to braided stainless-steel hoses for significantly longer life and burst resistance. Check connections for moisture, bulging, or corrosion at least twice a year.
          </p>

          {/* Tip 3 */}
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 flex items-center gap-3">
            <span className="w-8 h-8 flex items-center justify-center bg-sky-100 text-sky-600 rounded-full text-sm font-bold flex-shrink-0">3</span>
            Install Smart Water Leak Sensors
          </h2>
          <p className="text-gray-700 text-sm leading-relaxed mb-4 ml-11">
            While <strong>85% of homeowners</strong> have smoke detectors, fewer than <strong>17%</strong> have water sensors — despite water damage being far more common than fire. Place battery-powered leak detectors in high-risk areas: under sinks, behind toilets, near water heaters, and beside washing machines. Budget-friendly options start at just $15 per sensor.
          </p>
          <div className="bg-gray-50 rounded-xl p-5 mb-8 ml-11">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Recommended Products</p>
            <ul className="space-y-2">
              <li className="text-sm text-gray-700 flex items-start gap-2">
                <i className="ri-check-line text-sky-600 mt-0.5"></i>
                <span><strong>SwitchBot Wi-Fi Leak Detector</strong> — ~$15, smartphone alerts, no hub required</span>
              </li>
              <li className="text-sm text-gray-700 flex items-start gap-2">
                <i className="ri-check-line text-sky-600 mt-0.5"></i>
                <span><strong>GoveeLife Smart Water Leak Detector</strong> — ~$60 for multi-pack, Wi-Fi enabled</span>
              </li>
              <li className="text-sm text-gray-700 flex items-start gap-2">
                <i className="ri-check-line text-sky-600 mt-0.5"></i>
                <span><strong>Flo by Moen Smart Water Monitor</strong> — ~$500, whole-home monitoring with auto shut-off</span>
              </li>
            </ul>
          </div>

          {/* Tip 4 */}
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 flex items-center gap-3">
            <span className="w-8 h-8 flex items-center justify-center bg-sky-100 text-sky-600 rounded-full text-sm font-bold flex-shrink-0">4</span>
            Clean Gutters and Extend Downspouts
          </h2>
          <p className="text-gray-700 text-sm leading-relaxed mb-8 ml-11">
            Clogged gutters are one of the leading causes of foundation water intrusion. Clean them at least <strong>twice a year</strong> — in spring and fall. Ensure downspouts discharge water at least <strong>4 to 10 feet away</strong> from your foundation. Consider adding gutter guards to reduce debris buildup, especially if you have mature trees near your roofline.
          </p>

          {/* Tip 5 */}
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 flex items-center gap-3">
            <span className="w-8 h-8 flex items-center justify-center bg-sky-100 text-sky-600 rounded-full text-sm font-bold flex-shrink-0">5</span>
            Monitor Your Water Bill for Hidden Leaks
          </h2>
          <p className="text-gray-700 text-sm leading-relaxed mb-8 ml-11">
            An unexplained spike in your water bill is often the first sign of a hidden leak. The EPA estimates that fixing easily correctable household leaks can save homeowners about <strong>10% on monthly water bills</strong>. If your bill jumps without a change in usage, check your water meter: turn off all water in the house, wait 2 hours, and see if the meter has moved.
          </p>

          {/* Tip 6 */}
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 flex items-center gap-3">
            <span className="w-8 h-8 flex items-center justify-center bg-sky-100 text-sky-600 rounded-full text-sm font-bold flex-shrink-0">6</span>
            Maintain Proper Grading Around Your Foundation
          </h2>
          <p className="text-gray-700 text-sm leading-relaxed mb-8 ml-11">
            The ground around your home should slope <strong>away</strong> from the foundation at a rate of about 1 inch per foot for the first 6 feet. Over time, soil settles and grading can reverse, directing water toward your basement or crawl space. Check grading each spring after snowmelt and add soil where needed.
          </p>

          {/* Tip 7 */}
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 flex items-center gap-3">
            <span className="w-8 h-8 flex items-center justify-center bg-sky-100 text-sky-600 rounded-full text-sm font-bold flex-shrink-0">7</span>
            Insulate Pipes in Unheated Areas
          </h2>
          <p className="text-gray-700 text-sm leading-relaxed mb-8 ml-11">
            Pipes in attics, garages, crawl spaces, and exterior walls are vulnerable to freezing during Utah winters. Use foam pipe insulation sleeves (available at any hardware store for under $1 per foot) to protect exposed pipes. For extra protection in extreme cold, consider thermostatically controlled heat tape. We cover this in depth in our <Link to="/blog/winterizing-prevent-frozen-pipes" className="text-sky-600 hover:underline cursor-pointer">winterizing guide</Link>.
          </p>

          {/* Tip 8 */}
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 flex items-center gap-3">
            <span className="w-8 h-8 flex items-center justify-center bg-sky-100 text-sky-600 rounded-full text-sm font-bold flex-shrink-0">8</span>
            Inspect Your Roof Regularly
          </h2>
          <p className="text-gray-700 text-sm leading-relaxed mb-8 ml-11">
            Missing, cracked, or curling shingles allow water to penetrate your roof deck and cause hidden damage in attics and walls. Inspect your roof at least once a year and after every major storm. Pay special attention to flashing around chimneys, vents, and skylights — these are the most common leak points.
          </p>

          {/* Tip 9 */}
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 flex items-center gap-3">
            <span className="w-8 h-8 flex items-center justify-center bg-sky-100 text-sky-600 rounded-full text-sm font-bold flex-shrink-0">9</span>
            Keep Water Pressure Between 40–80 PSI
          </h2>
          <p className="text-gray-700 text-sm leading-relaxed mb-8 ml-11">
            Excessively high water pressure stresses pipes, fittings, and appliance connections, dramatically increasing the risk of leaks and burst pipes. Purchase a simple pressure gauge (under $10) that attaches to any hose bib. If your pressure exceeds 80 PSI, install a pressure-reducing valve — a plumber can do this for $150–$350.
          </p>

          {/* Tip 10 */}
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 flex items-center gap-3">
            <span className="w-8 h-8 flex items-center justify-center bg-sky-100 text-sky-600 rounded-full text-sm font-bold flex-shrink-0">10</span>
            Service Your Water Heater Annually
          </h2>
          <p className="text-gray-700 text-sm leading-relaxed mb-8 ml-11">
            Water heaters are one of the most common sources of catastrophic home water damage. Flush the tank annually to remove sediment buildup, check the pressure relief valve, and inspect the base for signs of corrosion or moisture. Most water heaters last <strong>8 to 12 years</strong> — if yours is approaching that age, consider proactive replacement before it fails.
          </p>

          {/* Emergency Checklist */}
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-10">
            <h3 className="text-lg font-bold text-red-800 mb-3 flex items-center gap-2">
              <i className="ri-alarm-warning-line text-xl"></i>
              If You Discover a Leak — Act Fast
            </h3>
            <ol className="space-y-2">
              <li className="text-sm text-gray-700 flex items-start gap-2">
                <span className="font-bold text-red-600 flex-shrink-0">1.</span>
                Shut off the water source or main valve immediately.
              </li>
              <li className="text-sm text-gray-700 flex items-start gap-2">
                <span className="font-bold text-red-600 flex-shrink-0">2.</span>
                Turn off electricity if water is near outlets or the breaker box.
              </li>
              <li className="text-sm text-gray-700 flex items-start gap-2">
                <span className="font-bold text-red-600 flex-shrink-0">3.</span>
                Document everything with photos and videos for insurance.
              </li>
              <li className="text-sm text-gray-700 flex items-start gap-2">
                <span className="font-bold text-red-600 flex-shrink-0">4.</span>
                Contact your insurer before starting major repairs.
              </li>
              <li className="text-sm text-gray-700 flex items-start gap-2">
                <span className="font-bold text-red-600 flex-shrink-0">5.</span>
                Call a certified restoration professional — <a href="tel:3853406632" className="text-sky-600 font-semibold hover:underline cursor-pointer">(385) 340-6632</a>.
              </li>
            </ol>
          </div>

          {/* Bottom Nav */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-8 border-t border-gray-200">
            <Link
              to="/blog"
              className="text-sm text-sky-600 font-semibold hover:underline cursor-pointer flex items-center gap-1.5"
            >
              <i className="ri-arrow-left-line"></i> Back to Blog
            </Link>
            <Link
              to="/blog/water-leak-detection-systems"
              className="text-sm text-sky-600 font-semibold hover:underline cursor-pointer flex items-center gap-1.5"
            >
              Next: Water Leak Detection Systems <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </article>

      {/* CTA */}
      <section className="py-14 bg-gray-50">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-3">Need Professional Help?</h3>
          <p className="text-gray-600 text-sm mb-6">
            Our IICRC-certified team is available 24/7 for emergency water damage restoration across Davis County, Utah.
          </p>
          <a
            href="tel:3853406632"
            className="inline-flex items-center gap-2 bg-sky-500 text-white px-8 py-3.5 rounded-lg text-sm font-semibold transition-all duration-200 hover:brightness-90 hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-phone-line"></i>
            Call Now: (385) 340-6632
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
